// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import FormDialog from '../../../components/Dialog/FormDialog';
import CheckboxInput from '../../../components/Form/CheckboxInput';
import TextInput from '../../../components/Form/TextInput';
import { useParties } from '../../../contexts/PartiesContext';
import { useRequestRegistrarService } from '../../../hooks/mutations/registry/onboarding/useRequestRegistrarService';
import { useRegistrarServiceRequests } from '../../../hooks/queries/registry/onboarding/useRegistrarServiceRequests';
import useRegistrarServices from '../../../hooks/queries/registry/onboarding/useRegistrarServices';
import useIsPackageAvailableFrom from '../../../hooks/queries/settings/useIsPackageAvailableFrom';

type DialogProps = {
  open: boolean;
  onClose: () => void;
};

export const RequestRegistrarServiceDialog = ({ open, onClose }: DialogProps) => {
  const [provider, setProvider] = useState<string>('');
  const [createTransferRule, setCreateTransferRule] = useState(true);
  const [createAllocationFactory, setCreateAllocationFactory] = useState(true);
  const requestRegistrarService = useRequestRegistrarService();

  // Check if service is already requested or provided
  const { party, operator } = useParties();
  const registrarServices = useRegistrarServices();
  const registrarServiceRequests = useRegistrarServiceRequests();
  const hasService = !!registrarServices.data?.find(
    (r) => r.payload.provider === provider && r.payload.registrar === party,
  );
  const hasPendingRequest = !!registrarServiceRequests.data?.find(
    (r) => r.payload.provider === provider && r.payload.registrar === party,
  );

  // Feature flag for preapprovals
  const enableOptionalFields = useIsPackageAvailableFrom(
    'utility-registry-app-v0',
    [operator, party],
    '0.6.0',
  );

  const resetState = useCallback(() => {
    setProvider('');
    setCreateTransferRule(true);
    setCreateAllocationFactory(true);
  }, [setProvider]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSubmit = useCallback(async () => {
    const payload = {
      argument: {
        operator,
        provider,
        registrar: party,
        createTransferRule: enableOptionalFields.data ? createTransferRule : null,
        createAllocationFactory: enableOptionalFields.data ? createAllocationFactory : null,
      },
    };
    await requestRegistrarService(payload);
    handleClose();
  }, [
    requestRegistrarService,
    provider,
    handleClose,
    operator,
    party,
    createTransferRule,
    createAllocationFactory,
    enableOptionalFields.data,
  ]);

  const isLoading =
    registrarServices.isLoading ||
    registrarServiceRequests.isLoading ||
    enableOptionalFields.isLoading;
  if (isLoading) return null;

  const disableSubmit = !provider || hasService || hasPendingRequest;

  return (
    <FormDialog
      open={open}
      title="Request Registrar Service"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Request"
      isSubmitDisabled={disableSubmit}
    >
      <TextInput label="Provider" value={provider} setValue={setProvider} required />
      {enableOptionalFields.data && (
        <>
          <CheckboxInput
            label="Create Transfer Rule"
            checked={createTransferRule}
            setChecked={setCreateTransferRule}
          />
          <CheckboxInput
            label="Create Allocation Factory"
            checked={createAllocationFactory}
            setChecked={setCreateAllocationFactory}
          />
        </>
      )}
    </FormDialog>
  );
};
